import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { register, confirmRegistration, resendConfirmationCode } from 'src/api/auth';
import { signupSchema, SignupFormData } from 'src/utils/validationSchemas';
import { PATH_AUTH } from 'src/routes/paths';

// Hardcoded signup code for validation
const VALID_SIGNUP_CODE = 'EQUALIQ2025';

export const useSignupForm: any = () => {
  const navigate = useNavigate();

  // Form setup
  const methods = useForm<SignupFormData>({
    resolver: yupResolver(signupSchema),
    defaultValues: {
      signupCode: "",
      email: "",
      password: "",
      confirmPassword: "",
      termsAccepted: false
    }
  });
  const { handleSubmit, watch } = methods;
  
  // Watch form values
  const watchedEmail = watch("email");
  
  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get the email to verify - either from form or from localStorage (if user refreshes page)
  const emailToVerify = watchedEmail || localStorage.getItem('pendingVerificationEmail') || '';

  // Helper functions
  const validateEmailForVerification = () => {
    if (!emailToVerify) {
      setErrorMessage("Email address is missing. Please go back to signup.");
      return null;
    }
    return emailToVerify;
  };

  // Event handlers
  const handleVerificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(e.target.value);
  };

  const handleResendVerification = async () => {
    const email = validateEmailForVerification();
    if (!email) return;
    
    try {
      setIsLoading(true);
      await resendConfirmationCode(email);
      toast.success("Verification code resent successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to resend verification code");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode) {
      setErrorMessage("Verification code is required");
      return;
    }
    
    const email = validateEmailForVerification();
    if (!email) return;
    
    try {
      setIsLoading(true);
      await confirmRegistration(email, verificationCode);
      toast.success("Account verified successfully");
      
      // Store the email in localStorage for the onboarding process
      localStorage.setItem('onboardingEmail', email);
      
      // Clear the pending verification email
      localStorage.removeItem('pendingVerificationEmail');
      
      // Navigate to onboarding page after successful verification
      navigate(PATH_AUTH.onboarding);
    } catch (error: any) {
      setErrorMessage(error.message || "Invalid verification code");
      toast.error(error.message || "Failed to verify account");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupError = (error: any) => {
    // Handle specific Cognito error messages
    let errorMessage = "Failed to create account. Please try again.";
    
    if (error.message && error.message.includes("UsernameExistsException")) {
      errorMessage = "An account with this email already exists.";
    } else if (error.message && error.message.includes("InvalidPasswordException")) {
      errorMessage = "Password does not meet requirements. Please use at least 8 characters with uppercase, lowercase, numbers and special characters.";
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    toast.error(errorMessage);
  };

  const handleSignup = async (formData: SignupFormData) => {
    try {
      // Validate signup code
      if (formData.signupCode !== VALID_SIGNUP_CODE) {
        toast.error("Invalid signup code. Please contact us to get a valid code.");
        return;
      }

      const success = await register(formData.email, formData.password);

      if (success) {
        // Store email for verification step
        localStorage.setItem('pendingVerificationEmail', formData.email);

        toast.success("Registration successful! Please check your email for verification code.");

        // Navigate to the await confirmation page
        navigate(PATH_AUTH.confirmation, {
          state: { email: formData.email }
        });
      } else {
        throw new Error("Registration failed");
      }
    } catch (error: any) {
      handleSignupError(error);
    }
  };

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      await handleSignup(data);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    // Form methods
    methods,
    onSubmit: handleSubmit(onSubmit),
    
    // Form data
    watchedEmail,
    emailToVerify,
    
    // State
    isLoading,
    showVerification,
    setShowVerification,
    verificationCode,
    errorMessage,
    setErrorMessage,
    
    // Handlers
    handleVerificationChange,
    handleVerification,
    handleResendVerification
  };
};